import { cn } from "@/lib/utils";
import {
  BRAND_ACCENT,
  MONOGRAM,
  WORDMARK,
  WORDMARK_ASPECT,
  wordmarkViewBox,
} from "./brand-geometry";

interface LogoProps {
  /** Mark size in pixels (the wordmark is sized to sit at the same weight). */
  size?: number;
  /** Render the "stayline" wordmark instead of the monogram tile. */
  withWordmark?: boolean;
  className?: string;
}

/** Wordmark height relative to the mark size it replaces. */
const WORDMARK_SCALE = 0.7;

/**
 * Brand lockup. With `withWordmark` it is the lowercase `stayline` wordmark
 * alone — the type carries the brand, the i's dot is the one accent — and is
 * labelled for screen readers. Without it, the `s.` monogram tile; that one is
 * decorative and callers provide their own surrounding label where needed.
 */
export function Logo({ size = 36, withWordmark = false, className }: LogoProps) {
  if (withWordmark) {
    const height = Math.round(size * WORDMARK_SCALE * 10) / 10;
    return (
      <span className={cn("inline-flex items-center", className)}>
        <Wordmark height={height} />
      </span>
    );
  }
  return (
    <span className={cn("inline-flex items-center", className)}>
      <Monogram size={size} />
    </span>
  );
}

/** `stayline` in brand ink (`currentColor`, so it follows the theme). */
export function Wordmark({ height, className }: { height: number; className?: string }) {
  const width = Math.round(height * WORDMARK_ASPECT * 10) / 10;
  return (
    <svg
      role="img"
      aria-label="Stayline"
      viewBox={wordmarkViewBox()}
      width={width}
      height={height}
      className={cn("shrink-0 text-foreground", className)}
    >
      <path d={WORDMARK.path} fill="currentColor" />
      <circle cx={WORDMARK.dot.cx} cy={WORDMARK.dot.cy} r={WORDMARK.dot.r} fill={BRAND_ACCENT} />
    </svg>
  );
}

/** The `s.` app-icon tile: ink tile, paper glyph, accent full stop. */
export function Monogram({
  size,
  className,
  dotClassName,
}: {
  size: number;
  className?: string;
  /** Hook for animating the accent dot (the loader breathes it). */
  dotClassName?: string;
}) {
  const { size: box, radius, transform, s, dot } = MONOGRAM;
  return (
    <svg
      aria-hidden="true"
      viewBox={`0 0 ${box} ${box}`}
      width={size}
      height={size}
      className={cn("shrink-0", className)}
    >
      <rect width={box} height={box} rx={radius} className="fill-foreground" />
      <g transform={transform}>
        <path d={s} className="fill-background" />
        <circle cx={dot.cx} cy={dot.cy} r={dot.r} fill={BRAND_ACCENT} className={dotClassName} />
      </g>
    </svg>
  );
}
